import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Toast } from 'primeng/toast';
import { filter, switchMap } from 'rxjs';
import { AccountService } from './services/account.service';
import { AuthService } from './services/auth.service';
import { DeviceService } from './services/device.service';
import { LogService } from './services/log.service';
import { PageLoaderService } from './services/page-loader.service';
import { ThemeService } from './services/theme.service';
import { WebsocketService } from './services/websocket.service';
import { NavigationComponent } from './shared/components/navigation/navigation.component';
import { GetAccountListResponseDto, GetAccountResponseDto } from './types/dtos/account.dto';
import { DeviceDto, DevicesResponseDto } from './types/dtos/device.dto';
import { LoggingActionEnum, LoggingTargetEnum } from './types/enums/logging.enum';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    NavigationComponent,
    Toast,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  public isLoginPage: boolean = false;

  private isInit: boolean = false;

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _router: Router,
    private readonly _themeService: ThemeService,
    private readonly _webSocketService: WebsocketService,
    private readonly _deviceService: DeviceService,
    private readonly _pageLoaderService: PageLoaderService,
    private readonly _authService: AuthService,
    private readonly _accountService: AccountService,
    private readonly _loggingService: LogService,
    private readonly _translateService: TranslateService,
  ) {
    this._translateService.addLangs([
      'lu',
      'de',
      'en',
      'fr',
    ]);
  }

  public ngOnInit(): void {
    this._themeService.initializeTheme();

    this._pageLoaderService.isLoading$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isLoading: boolean) => {
        if (isLoading) {
          document.body.classList.add('loading');
        } else {
          document.body.classList.remove('loading');
        }
      });

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((event) => {
        this._setLoginPageState((event as NavigationEnd).urlAfterRedirects);
        this._logPageChange();

        if (!this.isLoginPage) {
          this._loadInitialData();
        } else {
          this._pageLoaderService.setLoading(false);
        }
      });

    this._webSocketService.connect();
  }

  public ngOnDestroy(): void {
    this._webSocketService.disconnect();
  }

  private _setLoginPageState(url: string): void {
    this.isLoginPage = url === '/login';
  }

  private _logPageChange(): void {
    this._loggingService.sendLog({
      action: LoggingActionEnum.NAVIGATE,
      target: LoggingTargetEnum.PAGE,
    });
  }

  private async _loadInitialData(): Promise<void> {
    if (this.isInit) return;

    // await this._authService.waitUntilLogin();

    queueMicrotask(() => {
      this._deviceService.getAll()
        .pipe(
          switchMap((result: DevicesResponseDto) => {
            let devices: DeviceDto[] = result.data ?? [];

            this._deviceService.updateDeviceList(devices);

            return this._accountService.get();
          }),
          switchMap((result: GetAccountResponseDto) => {
            this._accountService.setAccount(result.data ?? null);
            if (result.data) {
              this._translateService.use(result.data.language);
            }

            return this._accountService.getAll();
          }),
        )
        .subscribe({
          next: (result: GetAccountListResponseDto) => {
            this._accountService.setAccountList(result.data ?? []);
          },
          complete: () => {
            this.isInit = true;
            this._authService.isLogin();
            this._pageLoaderService.setLoading(false);
          },
          error: (error) => {
            console.error('Error fetching data:', error);
          },
        });
    });
  }
}
